import { apiRequest, resolveApiUrl } from "./client";
import { isTauri } from "@/lib/is-tauri";
import {
  getDesktopChatAuthScope,
  captureDesktopChatAuthScopeGuard,
} from "@/desktop/desktop-chat-auth-scope";
import type { UploadedImage } from "./uploads";

const DB_NAME = "denker-local-images";
const STORE = "images";
const DEVICE_KEY = "denker.local-image-device.v1";
export const isLocalImageType = (type: string) =>
  /^image\/(png|jpeg|gif|webp)$/i.test(type);

export function localImageScope(): string {
  const scope = getDesktopChatAuthScope();
  if (!scope) throw new Error("Sign in before saving an attachment.");
  return `${resolveApiUrl("/")}\n${scope.accountId}`;
}

async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function browserImage(
  key: string,
  file?: File,
): Promise<File | undefined> {
  const db = await database();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(
        STORE,
        file ? "readwrite" : "readonly",
      );
      const request = file
        ? transaction.objectStore(STORE).put(file, key)
        : transaction.objectStore(STORE).get(key);
      let result: File | undefined;
      request.onsuccess = () => {
        result = file ?? request.result;
      };
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error("Local image storage failed."));
    });
  } finally {
    db.close();
  }
}

export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("Image could not be read."));
    reader.onerror = () =>
      reject(reader.error ?? new Error("Image could not be read."));
    reader.readAsDataURL(file);
  });
}

function browserDeviceId(): string {
  const saved = localStorage.getItem(DEVICE_KEY);
  if (saved) return saved;
  const id = crypto.randomUUID();
  localStorage.setItem(DEVICE_KEY, id);
  return id;
}

async function imageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () =>
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => reject(new Error("Image could not be decoded."));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function saveLocalImage(file: File): Promise<UploadedImage> {
  const scope = localImageScope();
  if (!isLocalImageType(file.type))
    throw new Error("Use a PNG, JPEG, GIF, or WebP image.");
  if (file.size > 5 * 1024 * 1024)
    throw new Error("Images must be 5 MB or smaller.");
  if (!file.size) throw new Error("Cannot attach an empty image.");
  const guard = captureDesktopChatAuthScopeGuard();
  const expectedAccountId = getDesktopChatAuthScope()!.accountId;
  const id = crypto.randomUUID();
  const dimensions = await imageDimensions(file);
  if (!guard()) throw new Error("The signed-in account changed.");
  let deviceId: string;
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    const dataUrl = await fileToDataUrl(file);
    if (!guard()) throw new Error("The signed-in account changed.");
    const stored = await invoke<{ device_id: string }>("store_local_image", {
      apiUrl: resolveApiUrl("/"),
      expectedAccountId,
      imageId: id,
      dataUrl,
      contentType: file.type,
    });
    deviceId = stored.device_id;
  } else {
    await browserImage(`${scope}\n${id}`, file);
    deviceId = `browser:${browserDeviceId()}`;
  }
  if (!guard()) throw new Error("The signed-in account changed.");
  const result = await apiRequest<{ data: UploadedImage }>(
    "/api/v1/uploads/local/",
    {
      method: "POST",
      requestGuard: guard,
      body: {
        id,
        device_id: deviceId,
        original_filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        ...dimensions,
      },
    },
  );
  return result.data;
}

export async function readLocalImage(id: string): Promise<string> {
  const scope = localImageScope();
  const guard = captureDesktopChatAuthScopeGuard();
  const expectedAccountId = getDesktopChatAuthScope()!.accountId;
  let result: string;
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    if (!guard()) throw new Error("The signed-in account changed.");
    result = await invoke<string>("read_local_image", {
      apiUrl: resolveApiUrl("/"),
      imageId: id,
      expectedAccountId,
    });
  } else {
    const file = await browserImage(`${scope}\n${id}`);
    if (!file)
      throw new Error(
        "This image is stored on the device where it was added. Enable screenshot sync there to access it here.",
      );
    result = await fileToDataUrl(file);
  }
  if (!guard()) throw new Error("The signed-in account changed.");
  return result;
}
